<template>
  <div class="tooltip" :data-tip="flashButtonTooltip">
    <button
      class="btn btn-accent w-full join-item"
      :class="[
        !webUsbSupported() || firmware.downloadUrl == null
          ? 'btn-disabled'
          : '',
        small ? '' : 'min-w-[10rem]',
      ]"
      @click="onFlash"
    >
      <Icon icon="ic:round-flash-on" class="text-lg" />
      {{ small ? "" : "Flash" }}
    </button>

    <VueFinalModal
      class="flex justify-center items-center"
      content-class="modal-box w-full md:max-w-3xl"
      :click-to-close="modalCancelable"
      :esc-to-close="modalCancelable"
      @closed="() => (stage = 'READY')"
      @update:modelValue="showModalUpdate"
      :modelValue="showModal"
      :hide-overlay="false"
    >
      <h1 class="font-semibold text-lg pl-1 mb-6">Keyboard firmware update</h1>

      <p class="pl-3 min-h-[1.5em] flex justify-start items-center gap-3">
        <Icon
          v-if="progress.info"
          icon="ic:baseline-info"
          class="scale-150 animate-pulse"
        />
        {{ progress.info == "" ? " " : progress.info }}
      </p>

      <div class="form-control mt-3">
        <label class="label h-10">
          <span class="label-text"> {{ progress.msg }} </span>
          <span class="label-text-alt"> {{ progress.note }} </span>
        </label>
        <progress
          class="progress"
          :class="detachWaitingUser ? 'animate-pulse' : ''"
          :value="progress.value"
          :max="progress.max"
        ></progress>
      </div>
    </VueFinalModal>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { WebDFU, WebDFUType } from "dfu";
import { computed, ref, watch } from "vue";
import { VueFinalModal } from "vue-final-modal";
import { useToast } from "vue-toastification";
import { err, delayMs, assertUnreachable, webUsbSupported } from "@/utils";
import { useFirmwareStore } from "@/stores";

defineProps<{
  small?: boolean;
}>();

const firmware = useFirmwareStore();
const toast = useToast();

type FlashingStage =
  | "READY"
  | "PREPARING"
  | "DETACHING"
  | "ERASING"
  | "WRITING"
  | "DONE";

type ProgressInfo = {
  info: string;
  msg: string;
  note: string;
  value: number;
  max: number;
};

const stage = ref<FlashingStage>("READY");
const showModal = ref(false);
const detachStep = ref(0);
const webdfuProgressValue = ref(0);
const webdfuProgressMax = ref(1);

const modalCancelable = computed(
  () => stage.value == "READY" || stage.value == "DONE"
);

const showModalUpdate = (v: boolean) => {
  if (v == true) showModal.value = v;
  else if (v == false && modalCancelable.value) showModal.value = v;
};

const DETACH_DURATION = 5000;
const DETACH_PROGRESS_UPDATE = 250;
const DETACH_UPDATE_STEPS = Math.ceil(DETACH_DURATION / DETACH_PROGRESS_UPDATE);

const runDetachTimer = async () => {
  for (let i = 0; i < DETACH_UPDATE_STEPS; i++) {
    await delayMs(DETACH_PROGRESS_UPDATE);
    if (stage.value == "DETACHING") {
      detachStep.value = Math.min(
        detachStep.value + 1,
        DETACH_UPDATE_STEPS - 1
      );
    } else {
      return;
    }
  }
};

const detachWaitingUser = computed(
  () =>
    stage.value == "DETACHING" && detachStep.value == DETACH_UPDATE_STEPS - 1
);

watch(stage, (newStage, _oldStage) => {
  if (newStage == "READY") {
    showModal.value = false;
    detachStep.value = 0;
    webdfuProgressValue.value = 0;
    webdfuProgressMax.value = 1;
  } else if (newStage == "PREPARING") showModal.value = true;
  else if (newStage == "DONE")
    setTimeout(() => {
      if (stage.value == "DONE") showModal.value = false;
    }, 700);
});

const progressInfo = (pi?: Partial<ProgressInfo>): ProgressInfo => ({
  msg: pi?.msg ?? "",
  info: pi?.info ?? "",
  note: pi?.note ?? "",
  value: pi?.value ?? 0,
  max: pi?.max ?? 1,
});

const progressKiloBytes = computed(
  () => (webdfuProgressValue.value / 1024).toFixed(1) + " kB"
);

const progress = computed((): ProgressInfo => {
  const s = stage.value;
  if (s == "READY") return progressInfo();
  else if (s == "PREPARING")
    return progressInfo({
      msg: "Preparing...",
      info: "Select USB device to use",
    });
  else if (s == "DETACHING")
    return progressInfo({
      msg: "Detaching...",
      info: "Select STM32 BOOTLOADER USB device when it appears",
      value: detachStep.value,
      max: DETACH_UPDATE_STEPS,
    });
  else if (s == "ERASING" || s == "WRITING")
    return progressInfo({
      msg: s == "ERASING" ? "Erasing..." : "Flashing...",
      info: "Do not disconnect the device",
      note: progressKiloBytes.value,
      value: webdfuProgressValue.value,
      max: webdfuProgressMax.value,
    });
  else if (s == "DONE")
    return progressInfo({
      msg: "Done",
      note: progressKiloBytes.value,
      value: 1,
    });
  else return assertUnreachable(s);
});

const flashButtonTooltip = computed(() => {
  if (!webUsbSupported()) return err("WebUSB is not supported by your browser");
  if (firmware.downloadUrl == "") return err("Compile the firmware first");
  return "Flash keyboard firmware";
});

const STM32_BOOTLOADER_VID_PID = [0x0483, 0xdf11];
const KEYBOARD_VID_PID = [0x16c0, 0x27db];

const vidPidFilter = (vidPid: number[]) => ({
  vendorId: vidPid[0],
  productId: vidPid[1],
});

// see: https://developer.mozilla.org/en-US/docs/Web/API/USBDevice
interface UsbDevice {
  productId: number;
  vendorId: number;
  productName: string;
  serialNumber: string;
}

const isBootloader = (dev: UsbDevice) =>
  dev.vendorId == STM32_BOOTLOADER_VID_PID[0] &&
  dev.productId == STM32_BOOTLOADER_VID_PID[1];

const webDfuLog = {
  info: (msg: string) => {
    console.log("WebDFU info", msg);
  },
  warning: (msg: string) => {
    console.log("WebDFU warning", msg);
  },
  progress: (done: number, total?: number) => {
    console.log("WebDFU progress", { done, total });
  },
};

const getDevice = (bootloaderOnly?: boolean) => {
  const filters = [vidPidFilter(STM32_BOOTLOADER_VID_PID)];
  if (bootloaderOnly != true) {
    filters.push(vidPidFilter(KEYBOARD_VID_PID));
  }

  try {
    return navigator.usb.requestDevice({ filters });
  } catch (err) {
    toast.error("Could not select device");
    throw err;
  }
};

const webDfuConnect = async (dev: UsbDevice) => {
  const webdfu = new WebDFU(dev, { forceInterfacesName: true }, webDfuLog);
  await webdfu.init();

  if (webdfu.interfaces.length == 0) {
    const msg = "Device does not have any USB DFU interfaces.";
    toast.error(msg);
    throw new Error(msg);
  }

  await webdfu.connect(0);

  return webdfu;
};

const detach = async (dev: UsbDevice) => {
  const webdfu = await webDfuConnect(dev);

  if (isBootloader(dev)) {
    throw new Error("Trying to detach bootloader");
  }

  try {
    await webdfu.detach();
  } catch (err) {
    toast.error(
      "Could not detach device. Did you press AllowBootloader key? (required in secure bootload mode)"
    );
    throw Error("Could not detach device: " + err);
  }
};

const getFirmware = async (): Promise<ArrayBuffer> => {
  const axiosInstance = axios.create();

  if (firmware.downloadUrl == undefined) {
    throw new Error("Firmware URL not available");
  }

  const result = await axiosInstance.get(firmware.downloadUrl, {
    responseType: "arraybuffer",
  });

  if (result.status != 200) {
    const msg = "Could not download firmware file";
    toast.error(msg);
    throw new Error(msg);
  }

  return result.data;
};

const flash = async (dev: UsbDevice) => {
  const webdfu = await webDfuConnect(dev);
  const firmware = await getFirmware();

  if (webdfu.type === WebDFUType.SDFUse && webdfu.dfuseMemoryInfo) {
    // console.log("DFUse segments:");
    // for (const segment of webdfu.dfuseMemoryInfo.segments) {
    //   console.log(segment);
    // }
    webdfu.dfuseStartAddress = 0x08000000;
  }

  // Need to clear error flags before downloading or else it fails
  try {
    if (await webdfu.isError()) {
      await webdfu.clearStatus();
    }
  } catch (error) {
    console.info("Failed to clear status");
  }

  // Should be retrieved from USB properties, but we provide the stm32 bootloader defautls just in case
  const transferSize = webdfu.properties?.TransferSize ?? 2048;
  const manifestationTolerant =
    webdfu.properties?.ManifestationTolerant ?? false;
  const process = webdfu.write(transferSize, firmware, manifestationTolerant);

  await new Promise<void>((resolve, reject) => {
    const progress = (done: number, total?: number) => {
      webdfuProgressValue.value = done;
      webdfuProgressMax.value = total ?? firmware.byteLength;
    };

    process.events.on("erase/start", () => (stage.value = "ERASING"));
    process.events.on("erase/process", progress);
    process.events.on("erase/end", () => null);

    process.events.on("write/start", () => (stage.value = "WRITING"));
    process.events.on("write/process", progress);
    process.events.on("write/end", (bytesSent: number) => {
      webdfuProgressMax.value = webdfuProgressValue.value = bytesSent;

      webdfu
        ?.getStatus()
        .then((status) => {
          console.log(
            `Final DFU status: state=${status.state}, status=${status.status}`
          );
        })
        .catch((error) => {
          console.error(error);
        });
    });

    // Promise fullfilment
    process.events.on("error", reject);
    process.events.on("end", () => {
      // NOTE: webdfu package seems to have error and not resolve the promise?
      // But we should be able to safely resolve flash() immediately.
      resolve();

      if (!manifestationTolerant) {
        webdfu
          .waitDisconnected(0)
          // .then(() => resolve)
          .catch(() => {
            // It didn't reset and disconnect for some reason...
            reject(new Error("Device unexpectedly tolerated manifestation."));
          });
      }
    });
  });
};

const onFlash = async () => {
  if (stage.value != "READY" && stage.value != "DONE") return;

  try {
    stage.value = "PREPARING";

    let dev = await getDevice();

    if (!isBootloader(dev)) {
      stage.value = "DETACHING";
      runDetachTimer(); // no await, run in background
      await detach(dev);
      dev = await getDevice(true);
    }

    await flash(dev);

    stage.value = "DONE";
    toast.success("Firmware successfully updated");
  } catch (e) {
    // console.trace({ e });
    stage.value = "READY";

    // Handle some errors
    if (e?.message.includes("No device selected")) {
      toast.warning("No device selected");
    } else if (
      e?.message.includes(
        "Must be handling a user gesture to show a permission request"
      )
    ) {
      console.error(e);
      toast.error(
        "Problem to get USB device due to browser safety features, try again."
      );
    } else if (e?.message.includes("Could not detach device")) {
      // toast message already shown
      console.error(e);
    } else {
      console.error(e);
      toast.error("Unexpected error");
    }
  }
};
</script>
